import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Plus, Edit, Trash2, ExternalLink, Printer, Search, Download, Volume2, Pencil } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { jsPDF } from 'jspdf';
import { isNativeAndroid, openBlobFileOnAndroid } from '@/utils/nativeAndroidFiles';

export default function Hinos() {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [viewingSong, setViewingSong] = useState(null);
  const [renamingVoice, setRenamingVoice] = useState(null);
  const [newVoiceLink, setNewVoiceLink] = useState({ name: '', url: '' });
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    key: '',
    spotify_link: '',
    youtube_link: '',
    lyrics: '',
    multitrack_url: '',
    voice_attachments: []
  });

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { data: songs = [], isLoading } = useQuery({
    queryKey: ['songs'],
    queryFn: () => base44.entities.Song.list('-created_date')
  });

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Song.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songs'] });
      toast.success('Hino cadastrado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Song.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songs'] });
      toast.success('Hino atualizado com sucesso!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Song.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songs'] });
      toast.success('Hino excluído com sucesso!');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      artist: '',
      key: '',
      spotify_link: '',
      youtube_link: '',
      lyrics: '',
      multitrack_url: '',
      voice_attachments: []
    });
    setEditingSong(null);
    setShowForm(false);
    setRenamingVoice(null);
    setNewVoiceLink({ name: '', url: '' });
  };

  const handleEdit = (song) => {
    setEditingSong(song);
    setFormData({
      title: song.title || '',
      artist: song.artist || '',
      key: song.key || '',
      spotify_link: song.spotify_link || '',
      youtube_link: song.youtube_link || '',
      lyrics: song.lyrics || '',
      multitrack_url: song.multitrack_url || '',
      voice_attachments: song.voice_attachments || []
    });
    setNewVoiceLink({ name: '', url: '' });
    setShowForm(true);
  };

  const isValidHttpUrl = (value) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const extractGoogleDriveFileId = (url) => {
    const value = String(url || '');
    const matchByFilePath = value.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (matchByFilePath?.[1]) return matchByFilePath[1];
    const matchByQuery = value.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (matchByQuery?.[1]) return matchByQuery[1];
    return null;
  };

  const toDownloadUrl = (url) => {
    const fileId = extractGoogleDriveFileId(url);
    if (fileId) return `https://drive.usercontent.google.com/download?id=${fileId}&export=download&confirm=t`;
    return url;
  };

  const getDownloadCandidates = (url) => {
    const fileId = extractGoogleDriveFileId(url);
    if (!fileId) return [url];
    return [
      `https://drive.usercontent.google.com/download?id=${fileId}&export=download&confirm=t`,
      `https://drive.google.com/uc?export=download&id=${fileId}&confirm=t`,
      `https://drive.google.com/uc?export=download&id=${fileId}`,
      url
    ];
  };

  const openExternalLink = (url) => {
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) window.location.href = url;
  };

  const downloadExternalLink = (url) => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(window.navigator?.userAgent || '');
    const candidates = getDownloadCandidates(url);
    const downloadUrl = candidates[0] || toDownloadUrl(url);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.target = isMobile ? '_self' : '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    if (isMobile && candidates[1]) {
      setTimeout(() => {
        window.location.href = candidates[1];
      }, 1800);
    }
  };

  const handleAddVoiceLink = () => {
    const name = String(newVoiceLink.name || '').trim();
    const url = String(newVoiceLink.url || '').trim();
    if (!url) {
      toast.error('Informe o link da voz.');
      return;
    }
    if (!isValidHttpUrl(url)) {
      toast.error('Link de voz inválido. Use URL completa (http/https).');
      return;
    }

    setFormData({
      ...formData,
      voice_attachments: [
        ...(formData.voice_attachments || []),
        { name: name || `Voz ${formData.voice_attachments.length + 1}`, url }
      ]
    });
    setNewVoiceLink({ name: '', url: '' });
  };

  const handleRemoveVoice = (index) => {
    const updated = formData.voice_attachments.filter((_, i) => i !== index);
    setFormData({ ...formData, voice_attachments: updated });
    toast.success('Anexo removido!');
  };

  const handleSubmit = () => {
    if (!formData.title) {
      toast.error('Titulo e obrigatorio.');
      return;
    }
    if (formData.multitrack_url && !isValidHttpUrl(formData.multitrack_url)) {
      toast.error('Link do multitrack invalido. Use URL completa (http/https).');
      return;
    }
    const invalidVoiceLink = (formData.voice_attachments || []).find((voice) => !isValidHttpUrl(voice?.url || ''));
    if (invalidVoiceLink) {
      toast.error('Existe link de voz invalido. Revise os links (http/https).');
      return;
    }

    if (editingSong) {
      updateMutation.mutate({ id: editingSong.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isNativePlatform = () =>
    typeof window !== 'undefined' &&
    typeof window.Capacitor?.isNativePlatform === 'function' &&
    window.Capacitor.isNativePlatform();

  const escapeHtml = (value) =>
    String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const sanitizeFilename = (value) =>
    String(value || 'hino')
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'hino';

  const buildPrintableHtml = (song) => `
    <html>
      <head>
        <title>${escapeHtml(song.title)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #111827; }
          h1 { margin-bottom: 8px; }
          .info { margin-bottom: 20px; color: #4b5563; }
          .lyrics { white-space: pre-wrap; font-family: monospace; line-height: 1.6; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(song.title)}</h1>
        <div class="info">
          ${song.artist ? `<p><strong>Interprete:</strong> ${escapeHtml(song.artist)}</p>` : ''}
          ${song.key ? `<p><strong>Tom:</strong> ${escapeHtml(song.key)}</p>` : ''}
        </div>
        <div class="lyrics">${escapeHtml(song.lyrics || 'Cifra nao disponivel')}</div>
        <script>
          window.addEventListener('afterprint', function () {
            window.close();
            if (window.opener && !window.opener.closed) {
              window.opener.focus();
            }
          });
        </script>
      </body>
    </html>
  `;

  const buildSongPdfBlob = (song) => {
    const doc = new jsPDF({
      unit: 'pt',
      format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    let cursorY = margin;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(song.title || 'Hino', margin, cursorY);
    cursorY += 24;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    if (song.artist) {
      doc.text(`Interprete: ${song.artist}`, margin, cursorY);
      cursorY += 16;
    }

    if (song.key) {
      doc.text(`Tom: ${song.key}`, margin, cursorY);
      cursorY += 16;
    }

    cursorY += 8;
    doc.setFont('courier', 'normal');
    doc.setFontSize(10);

    const lines = doc.splitTextToSize(song.lyrics || 'Cifra nao disponivel', contentWidth);
    const lineHeight = 14;

    lines.forEach((line) => {
      if (cursorY > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }

      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });

    return doc.output('blob');
  };

  const handlePrint = async (song) => {
    const pdfFilename = `${sanitizeFilename(song.title)}.pdf`;

    if (isNativeAndroid()) {
      try {
        const pdfBlob = buildSongPdfBlob(song);
        await openBlobFileOnAndroid(pdfBlob, pdfFilename, 'application/pdf');
        return;
      } catch (error) {
        console.error('Falha ao abrir PDF no Android:', error);
      }
    }

    if (isNativePlatform()) {
      try {
        const pdfBlob = buildSongPdfBlob(song);
        const pdfFile = new File([pdfBlob], pdfFilename, { type: 'application/pdf' });

        if (navigator.share && navigator.canShare?.({ files: [pdfFile] })) {
          await navigator.share({
            title: song.title || 'Hino',
            text: `Cifra do hino ${song.title || ''}`.trim(),
            files: [pdfFile],
          });
          return;
        }

        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        return;
      } catch (error) {
        console.error('Falha ao compartilhar PDF no dispositivo:', error);
      }
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Nao foi possivel abrir a janela de impressao. Verifique o bloqueador de pop-ups.');
      return;
    }

    printWindow.document.write(buildPrintableHtml(song));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 150);
  };

  const filteredSongs = useMemo(() => {
    const normalizedSearch = search.toLowerCase();

    return songs
      .filter((song) =>
        song.title?.toLowerCase().includes(normalizedSearch) ||
        song.artist?.toLowerCase().includes(normalizedSearch)
      )
      .sort((a, b) =>
        (a.title || '').localeCompare(b.title || '', 'pt-BR', { sensitivity: 'base' })
      );
  }, [songs, search]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Hinos" 
            subtitle={`${songs.length} cadastrados`}
            showBack={true}
            action={
              isAdmin && (
                <Button onClick={() => setShowForm(true)} size="sm" className="bg-white text-purple-700 hover:bg-purple-50">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              )
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar hino ou intérprete"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Songs List */}
        {filteredSongs.length === 0 ? (
          <EmptyState
            icon={Music}
            title="Nenhum hino encontrado"
            description="Comece adicionando hinos ao repertório"
            actionLabel={isAdmin ? "Adicionar Hino" : undefined}
            onAction={isAdmin ? () => setShowForm(true) : undefined}
          />
        ) : (
          <div className="space-y-3">
            {filteredSongs.map((song, index) => (
              <motion.div
                key={song.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">{song.title}</h3>
                    {song.artist && <p className="text-sm text-gray-500">{song.artist}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {song.key && (
                        <span className="inline-block text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          Tom: {song.key}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setViewingSong(song)}>
                      <Music className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(song)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-red-600"
                          onClick={() => {
                            if (confirm('Deseja excluir este hino?')) {
                              deleteMutation.mutate(song.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingSong ? 'Editar Hino' : 'Novo Hino'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cantor/Intérprete</Label>
                <Input
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                />
              </div>
              <div>
                <Label>Tom</Label>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  placeholder="Ex: C, D, E, F#m"
                />
              </div>
            </div>

            <div>
              <Label>Link do Spotify</Label>
              <Input
                value={formData.spotify_link}
                onChange={(e) => setFormData({ ...formData, spotify_link: e.target.value })}
                placeholder="https://open.spotify.com/..."
              />
            </div>

            <div>
              <Label>Link do YouTube</Label>
              <Input
                value={formData.youtube_link}
                onChange={(e) => setFormData({ ...formData, youtube_link: e.target.value })}
                placeholder="https://youtube.com/..."
              />
            </div>

            <div>
              <Label>Cifra</Label>
              <Textarea
                value={formData.lyrics}
                onChange={(e) => setFormData({ ...formData, lyrics: e.target.value })}
                rows={10}
                placeholder="Cole aqui a cifra da música..."
              />
            </div>

                        <div>
              <Label>Link do Multitrack</Label>
              <div className="mt-2 space-y-2">
                <Input
                  value={formData.multitrack_url}
                  onChange={(e) => setFormData({ ...formData, multitrack_url: e.target.value })}
                  placeholder="https://drive.google.com/..."
                />
                {formData.multitrack_url && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(formData.multitrack_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Abrir Multitrack
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Vozes (Links Externos)</Label>
              <div className="mt-2 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    value={newVoiceLink.name}
                    onChange={(e) => setNewVoiceLink((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Nome (opcional)"
                  />
                  <div className="sm:col-span-2 flex gap-2">
                    <Input
                      value={newVoiceLink.url}
                      onChange={(e) => setNewVoiceLink((prev) => ({ ...prev, url: e.target.value }))}
                      placeholder="https://drive.google.com/..."
                    />
                    <Button type="button" onClick={handleAddVoiceLink}>
                      Adicionar
                    </Button>
                  </div>
                </div>

                {formData.voice_attachments?.map((voice, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <Volume2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
                    {renamingVoice === index ? (
                      <Input
                        autoFocus
                        value={voice.name}
                        onChange={(e) => {
                          const updated = [...formData.voice_attachments];
                          updated[index] = { ...updated[index], name: e.target.value };
                          setFormData({ ...formData, voice_attachments: updated });
                        }}
                        onBlur={() => setRenamingVoice(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setRenamingVoice(null);
                          }
                        }}
                        className="h-8 text-sm"
                      />
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{voice.name}</p>
                        <a
                          href={voice.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Abrir link da voz
                        </a>
                      </div>
                    )}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => setRenamingVoice(index)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => handleRemoveVoice(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingSong ? 'Salvar' : 'Cadastrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Song Dialog */}
      <AnimatePresence>
        {viewingSong && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4"
            onClick={() => setViewingSong(null)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl w-full max-w-lg p-6 max-h-[85vh] overflow-auto"
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />
              
              <h2 className="text-xl font-bold text-gray-900">{viewingSong.title}</h2>
              {viewingSong.artist && <p className="text-gray-600 mt-1">{viewingSong.artist}</p>}
              {viewingSong.key && (
                <span className="inline-block mt-2 text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded">
                  Tom: {viewingSong.key}
                </span>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                {viewingSong.spotify_link && (
                  <Button size="sm" variant="outline" onClick={() => window.open(viewingSong.spotify_link, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Spotify
                  </Button>
                )}
                {viewingSong.youtube_link && (
                  <Button size="sm" variant="outline" onClick={() => window.open(viewingSong.youtube_link, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-1" />
                    YouTube
                  </Button>
                )}
                {viewingSong.multitrack_url && (
                  <Button size="sm" variant="outline" className="bg-purple-50" onClick={() => downloadExternalLink(viewingSong.multitrack_url)}>
                    <Download className="w-4 h-4 mr-1" />
                    Baixar Multitrack
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => handlePrint(viewingSong)}>
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimir
                </Button>
              </div>

              {viewingSong.multitrack_url && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Multitrack</h3>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExternalLink(viewingSong.multitrack_url)}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Abrir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadExternalLink(viewingSong.multitrack_url)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Baixar
                    </Button>
                  </div>
                </div>
              )}


              {viewingSong.voice_attachments?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Vozes ({viewingSong.voice_attachments.length})</h3>
                  <div className="space-y-3">
                    {viewingSong.voice_attachments.map((voice, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Volume2 className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-900">{voice.name}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openExternalLink(voice.url)}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Abrir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadExternalLink(voice.url)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Baixar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingSong.lyrics && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Cifra</h3>
                  <pre className="bg-gray-50 p-4 rounded-xl text-sm whitespace-pre-wrap font-mono">
                    {viewingSong.lyrics}
                  </pre>
                </div>
              )}

              <Button 
                className="w-full mt-6"
                onClick={() => setViewingSong(null)}
              >
                Fechar
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}






